import React, { useMemo } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { enCA } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import "./DateInput.css";

registerLocale("en-CA", enCA);

export const COMPACT_DATE_INPUT_CLASS =
  "h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 transition-colors placeholder:text-gray-400 hover:border-gray-300 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300";

function parseYmd(value) {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatYmd(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getYearOptions(minDate, maxDate) {
  const currentYear = new Date().getFullYear();
  const startYear = minDate?.getFullYear?.() ?? currentYear - 50;
  const endYear = maxDate?.getFullYear?.() ?? currentYear + 10;
  return Array.from(
    { length: Math.max(1, endYear - startYear + 1) },
    (_, index) => startYear + index,
  );
}

/** Date picker with Monday as the first column (Mo–Su). Value/onChange use YYYY-MM-DD strings. */
export default function DateInput({
  id,
  name,
  value,
  onChange,
  className,
  disabled = false,
  required = false,
  min,
  max,
  placeholder,
  isClearable = true,
  ...rest
}) {
  const selected = useMemo(() => parseYmd(value), [value]);
  const minDate = useMemo(() => parseYmd(min), [min]);
  const maxDate = useMemo(() => parseYmd(max), [max]);
  const yearOptions = useMemo(
    () => getYearOptions(minDate, maxDate),
    [minDate, maxDate],
  );

  return (
    <DatePicker
      id={id}
      name={name}
      selected={selected}
      onChange={(date) => onChange?.(date ? formatYmd(date) : "")}
      disabled={disabled}
      required={required}
      minDate={minDate}
      maxDate={maxDate}
      calendarStartDay={1}
      locale="en-CA"
      dateFormat="MMM d, yyyy"
      placeholderText={placeholder ?? "Select Date..."}
      fixedHeight
      showPopperArrow={false}
      popperPlacement="bottom-start"
      popperClassName="atlas-date-picker-popper"
      portalId="root"
      className={className ?? COMPACT_DATE_INPUT_CLASS}
      wrapperClassName="atlas-date-input-wrapper"
      isClearable={isClearable && !disabled}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <div className="atlas-date-picker-header">
          <button
            type="button"
            className="atlas-date-picker-nav"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="atlas-date-picker-selects">
            <select
              className="atlas-date-picker-select"
              value={date.getMonth()}
              onChange={(event) => changeMonth(Number(event.target.value))}
              aria-label="Select month"
            >
              {MONTH_OPTIONS.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <select
              className="atlas-date-picker-select atlas-date-picker-year"
              value={date.getFullYear()}
              onChange={(event) => changeYear(Number(event.target.value))}
              aria-label="Select year"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="atlas-date-picker-nav"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      )}
      {...rest}
    />
  );
}
